import type { ContentDetailsSource } from '@/services/contentDetailsService';
import type { MovieGenre, TVShowGenre } from '@/types/model';

export interface GenreMappingResult<T> {
  genres: T[];
  unmappedGenres: string[];
}

type GenreMap<T> = Record<string, readonly T[]>;

const movieGenreMap: GenreMap<MovieGenre> = {
  action: ['ACTION'],
  adventure: ['ADVENTURE'],
  animation: ['ANIMATION'],
  biography: ['BIOGRAPHY'],
  comedy: ['COMEDY'],
  crime: ['CRIME'],
  documentary: ['DOCUMENTARY'],
  drama: ['DRAMA'],
  family: ['FAMILY'],
  fantasy: ['FANTASY'],
  history: ['HISTORY'],
  horror: ['HORROR'],
  music: ['MUSIC'],
  musical: ['MUSICAL'],
  mystery: ['MYSTERY'],
  romance: ['ROMANCE'],
  'science fiction': ['SCI_FI'],
  'sci fi': ['SCI_FI'],
  thriller: ['THRILLER'],
  war: ['WAR'],
  western: ['WESTERN'],
};

const tvShowGenreMap: GenreMap<TVShowGenre> = {
  action: ['ACTION'],
  adventure: ['ADVENTURE'],
  'action adventure': ['ACTION', 'ADVENTURE'],
  animation: ['ANIMATION'],
  biography: ['BIOGRAPHY'],
  comedy: ['COMEDY'],
  crime: ['CRIME'],
  documentary: ['DOCUMENTARY'],
  drama: ['DRAMA'],
  family: ['FAMILY'],
  fantasy: ['FANTASY'],
  history: ['HISTORY'],
  horror: ['HORROR'],
  kids: ['FAMILY'],
  music: ['MUSIC'],
  musical: ['MUSICAL'],
  mystery: ['MYSTERY'],
  news: ['NEWS'],
  reality: ['REALITY'],
  romance: ['ROMANCE'],
  'science fiction': ['SCI_FI'],
  'sci fi': ['SCI_FI'],
  'sci fi fantasy': ['SCI_FI', 'FANTASY'],
  soap: ['SOAP_OPERA'],
  'soap opera': ['SOAP_OPERA'],
  sport: ['SPORT'],
  talk: ['TALK_SHOW'],
  'talk show': ['TALK_SHOW'],
  thriller: ['THRILLER'],
  war: ['WAR'],
  'war politics': ['WAR'],
  western: ['WESTERN'],
};

export function mapExternalMovieGenres(
  genres: string[],
  source: ContentDetailsSource,
): GenreMappingResult<MovieGenre> {
  return mapGenres(genres, movieGenreMap, source, 'movie');
}

export function mapExternalTVShowGenres(
  genres: string[],
  source: ContentDetailsSource,
): GenreMappingResult<TVShowGenre> {
  return mapGenres(genres, tvShowGenreMap, source, 'TV show');
}

function mapGenres<T extends string>(
  externalGenres: string[],
  genreMap: GenreMap<T>,
  source: ContentDetailsSource,
  contentType: string,
): GenreMappingResult<T> {
  const mapped = new Set<T>();
  const unmappedGenres: string[] = [];

  externalGenres.forEach((externalGenre) => {
    const internalGenres = genreMap[normalizeGenre(externalGenre)];
    if (!internalGenres) {
      unmappedGenres.push(externalGenre);
      return;
    }
    internalGenres.forEach((genre) => mapped.add(genre));
  });

  if (unmappedGenres.length > 0) {
    console.warn(`Unmapped ${source} ${contentType} genres:`, unmappedGenres);
  }

  return { genres: [...mapped], unmappedGenres };
}

function normalizeGenre(genre: string): string {
  return genre
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/&/g, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');
}
